/**
 * Background Build Area Tool
 * 
 * Tool for building one area on a slide using the Python backend (Aspose.Slides).
 * Called by ExecutionAgent for each area it decides to build during Big Slide Gen.
 * 
 * This tool operates on a BACKGROUND PPTX file, not the live PowerPoint presentation.
 * For live presentation editing, use LiveBuildAreaTool instead.
 * 
 * Makes HTTP POST to /plans/{plan_id}/build/area endpoint.
 * Returns screenshot for LLM to evaluate the result.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { backendClient } from "../../../../../services/communication/backendClient";
import {
    BuildAreaParamsSchema,
    type BuildAreaParams,
    type BuildAreaResponse,
} from "./backgroundBuildArea.schema";

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const BACKGROUND_BUILD_AREA_SYSTEM_PROMPT = `### buildArea
Build one area on a slide (Background mode - operates on PPTX file).

**When to use:**
- Building specific regions during Big Slide Gen
- Creating headers, content, charts, tables, footers

**Build ONE area at a time** and evaluate the screenshot result.

**Returns:** Screenshot for you to evaluate if the area looks correct.

\`\`\`json
{
  "slideIndex": 0,
  "areaDescription": "Header with title",
  "areaSpec": {
    "targetArea": { "left": 50, "top": 50, "width": 600, "height": 100 },
    "collections": [
      {
        "type": "createShape",
        "shapeType": "textbox",
        "position": { "left": 50, "top": 50, "width": 600, "height": 60 },
        "textContent": { "paragraphs": [{ "setText": "Title Text" }] }
      }
    ]
  }
}
\`\`\``;

// ═══════════════════════════════════════════════════════════════════════════
// BACKGROUND BUILD AREA TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class BackgroundBuildAreaTool extends ToolBase<BuildAreaParams, BuildAreaResponse> {
    public readonly name = "buildArea";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = BACKGROUND_BUILD_AREA_SYSTEM_PROMPT;
    
    /** Plan ID for this build session */
    private planId: string;
    
    constructor(planId: string) {
        super();
        this.planId = planId;
        
        this.description = new ToolDescription(
            "buildArea",
            `Build one area on a slide. Creates shapes, text, tables, charts, or copies elements.

Use this tool to build specific regions of a slide:
- Header area: Title and subtitle text
- Body area: Main content (text boxes, bullet points)
- Chart area: Charts linked to Excel data
- Table area: Data tables
- Footer area: Footer text, page numbers

The tool returns a screenshot so you can evaluate if the area looks correct.
Each call auto-checkpoints the presentation (5-digit ID for rollback).

IMPORTANT:
- Build ONE area at a time
- Evaluate the screenshot after each build
- If something looks wrong, you can continue building to fix it
- Call submitResults when the section is complete

Example for a title area:
{
  "slideIndex": 0,
  "areaDescription": "Header with title and subtitle",
  "areaSpec": {
    "targetArea": { "left": 50, "top": 50, "width": 600, "height": 100 },
    "collections": [
      {
        "type": "createShape",
        "shapeType": "textbox",
        "position": { "left": 50, "top": 50, "width": 600, "height": 60 },
        "textContent": {
          "paragraphs": [{ "setText": "Quarterly Results" }]
        }
      }
    ]
  }
}`,
            BuildAreaParamsSchema
        );
    }
    
    /**
     * Execute the buildArea tool by calling the Python backend
     */
    public async execute(parameters: unknown): Promise<BuildAreaResponse> {
        const parseResult = safeParseToolParams(BuildAreaParamsSchema, parameters);
        
        if (!parseResult.success) {
            console.error('[BackgroundBuildAreaTool] Invalid parameters:', parseResult.error);
            return {
                success: false,
                checkpointId: "00000",
                screenshotBase64: null,
                shapesCreated: [],
                shapeCount: 0,
                errors: [`Invalid parameters: ${parseResult.error}`],
            };
        }
        
        const params = parseResult.data;
        
        console.log(`[BackgroundBuildAreaTool] Building area on slide ${params.slideIndex}: ${params.areaDescription}`);
        
        try {
            // Call Python backend
            const response = await backendClient.buildArea(
                this.planId,
                params.slideIndex,
                params.areaSpec
            );
            
            console.log(`[BackgroundBuildAreaTool] Area built successfully, checkpoint: ${response.checkpoint_id}`);
            
            return {
                success: response.success,
                checkpointId: response.checkpoint_id,
                screenshotBase64: response.screenshot_base64 || null,
                shapesCreated: response.shapes_created || [],
                shapeCount: response.shape_count || 0,
                errors: response.errors || [],
                warnings: response.warnings || [],
            };
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[BackgroundBuildAreaTool] Error: ${errorMessage}`);
            
            return {
                success: false,
                checkpointId: "00000",
                screenshotBase64: null,
                shapesCreated: [],
                shapeCount: 0,
                errors: [errorMessage],
            };
        }
    }
}

export default BackgroundBuildAreaTool;


